import { useMemo, useState } from 'react';
import { Container } from '@/components/Container';
import { FilterPills } from '@/components/FilterPills';
import { PageHeader } from '@/components/PageHeader';
import { CircularItem } from '@/sections/circulars/CircularItem';
import { ListError, ListSkeleton } from '@/sections/circulars/CircularsList';
import { useCirculars } from '@/sections/circulars/useCirculars';
import { CATEGORY_LABEL, categoriesPresent, groupByYear } from '@/lib/circulars';

const PAGE_SIZE = 20;
const BAND = 'm-0 px-6 py-2 font-mono text-[0.75rem] tracking-[0.08em] uppercase';

export function Notices() {
  const { status, data, retry } = useCirculars();
  const [category, setCategory] = useState('All');
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState(PAGE_SIZE);

  // Filters come from the notices themselves: a category appears once a notice
  // uses it, so staff never maintain this list.
  const options = useMemo(() => ['All', ...categoriesPresent(data).map((c) => CATEGORY_LABEL[c])], [data]);

  const q = query.trim().toLowerCase();
  const matches = data.filter(
    (c) => (category === 'All' || CATEGORY_LABEL[c.category] === category) && (!q || c.title.toLowerCase().includes(q)),
  );
  const pinned = matches.filter((c) => c.isPinned);
  const rest = matches.filter((c) => !c.isPinned);
  const groups = groupByYear(rest.slice(0, limit));

  const noun = (n: number) => `${n} notice${n === 1 ? '' : 's'}`;
  const narrowed = category !== 'All' || q !== '';
  const count = narrowed ? `${matches.length} of ${noun(data.length)}` : noun(data.length);

  const reset = () => setLimit(PAGE_SIZE);

  return (
    <>
      <PageHeader
        title="Notice board"
        intro="Examination circulars, fee charts and official notices from ITES."
      />
      <Container className="pt-[clamp(40px,6vw,72px)] pb-[clamp(56px,7vw,96px)]">
        <div className="border border-rule bg-white">
          <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 border-b border-rule p-6">
            <FilterPills
              label="Filter notices"
              options={options}
              value={category}
              onChange={(c) => {
                setCategory(c);
                reset();
              }}
            />
            <label className="flex min-w-0 flex-[0_1_320px]">
              <span className="sr-only">Search notices</span>
              <input
                type="search"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  reset();
                }}
                placeholder="Search notices"
                className="min-h-11 min-w-0 flex-1 rounded-control border border-input-border bg-white px-3.5 text-[1rem]"
              />
            </label>
          </div>

          {status === 'loading' && <ListSkeleton rows={8} layout="full" />}
          {status === 'error' && <ListError onRetry={retry} className="p-6" />}

          {status === 'success' && (
            <>
              <p aria-live="polite" className="m-0 border-b border-rule px-6 py-3 text-[0.875rem] text-muted">
                {count}
              </p>

              {matches.length === 0 && (
                <p className="m-0 px-6 py-8 text-[1.0625rem] text-muted">
                  {data.length === 0 ? 'No notices have been published yet.' : 'No notices match your search.'}
                </p>
              )}

              {pinned.length > 0 && (
                <section aria-labelledby="pinned-title" className="border-b border-rule bg-sky-50">
                  <h2 id="pinned-title" className={`${BAND} pt-4 pb-1 text-teal-700`}>Pinned</h2>
                  <ul aria-label="Pinned notices" className="m-0 list-none p-0 [&>li:last-child]:border-b-0">
                    {pinned.map((c) => (
                      <CircularItem key={c.id} circular={c} layout="full" pinned />
                    ))}
                  </ul>
                </section>
              )}

              {groups.map((g) => (
                <section key={g.year} aria-labelledby={`year-${g.year}`}>
                  <h2 id={`year-${g.year}`} className={`${BAND} border-b border-rule bg-paper text-muted`}>
                    {g.year}
                  </h2>
                  <ul aria-label={`Notices from ${g.year}`} className="m-0 list-none p-0 [&>li:last-child]:border-b-0">
                    {g.items.map((c) => (
                      <CircularItem key={c.id} circular={c} layout="full" />
                    ))}
                  </ul>
                </section>
              ))}

              {rest.length > PAGE_SIZE && rest.length > limit && (
                <div className="border-t border-rule p-6">
                  <button
                    type="button"
                    onClick={() => setLimit((l) => l + PAGE_SIZE)}
                    className="box-content min-h-11 cursor-pointer rounded-control border-[1.5px] border-navy-800 bg-white px-[22px] font-semibold text-navy-800"
                  >
                    Show more notices
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </Container>
    </>
  );
}
